import type { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import cashBoxApi from "../dataProvider/cashBoxApi";
import { History } from "../app/router/navigation";

declare type CashBoxApiResponse<DataType> = AxiosResponse<ApiResult<DataType>>;
declare type CashBoxApiCallResult<DataType> = CashBoxApiResponse<DataType> | AxiosError;

type ApiErrorResponseData = {
    errors?: ApiErrorMessage;
    status?: boolean;
};

const isRecord = (value: unknown): value is Record<string, unknown> => (
    typeof value === "object" && value !== null
);

const AUTH_ERROR_SEARCH_PARAM = "auth_err";
const AUTH_TOKEN_CLEARED_EVENT = "cashbox-auth-token-cleared";

const isAuthRequest = (url?: string): boolean => {
    const normalizedUrl = `${url || ""}`.toLowerCase();
    return normalizedUrl.includes("/auth/");
};

const redirectToLoginPage = () => {
    const currSP = new URLSearchParams(window.location.search);
    currSP.delete(AUTH_ERROR_SEARCH_PARAM);
    currSP.set(AUTH_ERROR_SEARCH_PARAM, "true");
    const currSP_str = currSP.toString();
    const navigate = (History as { navigate?: ((params: { pathname: string; search: string }) => void) | null }).navigate;

    // window.location.href = `/login${currSP_str ? "?" + currSP_str : ""}`;
    if (navigate) {
        navigate({
            pathname: "/login",
            search: currSP_str ? `?${currSP_str}` : "",
        });
    }
};

export const convertToCustomErrorType = (err: unknown = {}) : ApiError => {
    const errorRecord = isRecord(err) ? err : {};
    const response = isRecord(errorRecord.response) ? errorRecord.response : {};
    const responseData = isRecord(response.data) ? response.data as ApiErrorResponseData : {};

    return {
        code: typeof errorRecord.code === "string" ? errorRecord.code : undefined,
        message: typeof errorRecord.message === "string" ? errorRecord.message : "",
        details: {
            errors: responseData.errors
        },
        status: responseData.status,
        statusCode: typeof response.status === "number" ? response.status : undefined,
        statusText: typeof response.statusText === "string" ? response.statusText : undefined,
    };
};

export const callCashBoxApi = async <DataType>(
    config: AxiosRequestConfig = {}
): Promise<CashBoxApiCallResult<DataType>> => {
    let response: CashBoxApiResponse<DataType>;

    config = config || {};
    config.headers = config.headers || {};
    const authToken = localStorage.getItem('auth_token');
    if (authToken) {
        config.headers['Authorization'] = `Bearer ${authToken}`;
    }

    try {
        response = await cashBoxApi<ApiResult<DataType>>(config);
    } catch (err) {
        const typedErr = err as AxiosError;
        console.error(
            // `API ${finalConfig.method} request to ${finalConfig.url} failed`,
            `API ${config.method} request to ${typedErr.request?.responseURL} failed`,
            convertToCustomErrorType(typedErr)
        );

        if (
            typedErr?.response?.status === 401 &&
            authToken &&
            !isAuthRequest(config.url) &&
            !window.location.pathname.startsWith("/login")
        ) {
            localStorage.removeItem("auth_token");
            window.dispatchEvent(new Event(AUTH_TOKEN_CLEARED_EVENT));
            redirectToLoginPage();
        }

        return typedErr;
    } finally {
        // 
    }

    return response;
};
